/// <reference path="./stripe.d.ts" />

import { Injectable } from '@angular/core';
import { AppConfig } from '../common';

export class CreateStripeTokenResponse {
    constructor(status : number, response : StripeTokenResponse) {
        this.status = status;
        this.response = response;
    }
    status : number;
    response : StripeTokenResponse;
}

export class GetStripeTokenResponse {
    constructor(status : number, response : StripeTokenResponse) {
        this.status = status;
        this.response = response;
    }
    status : number;
    response : StripeTokenResponse;
}

/**
 * Server-side rendering capable Stripe implementation wrapped around Stripe.js on the client side,
 * throwing exceptions when on server-side.
 */
@Injectable()
export class StripeService {

    constructor(
        appConfig : AppConfig
    ) {
        this.setPublishableKey(appConfig.stripePublishableKey);
    }

    get available(): boolean {
        return typeof Stripe !== 'undefined'
    }

    get applePay(): StripeApplePay {
        if (!this.available)
            return null;
        
        return Stripe.applePay;
    }

    private publishableKey : string;

    async loadApi(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (typeof Stripe !== 'undefined') {
                resolve();
                return;
            }

            if (typeof window === 'undefined')
                throw new Error("StripeService cannot be used server-side (yet)");

            let scriptEl : HTMLScriptElement = document.createElement('script');
            scriptEl.src = "https://js.stripe.com/v2/";
            scriptEl.addEventListener('load', () => {

                if (typeof Stripe !== 'undefined')
                    resolve();
                else 
                    reject();
            });
            scriptEl.addEventListener('error', () => {
                reject();
            })

            document.body.appendChild(scriptEl);
        });
    }

    async apiReady(): Promise<void> {
        if (typeof Stripe === 'undefined')
            await this.loadApi();

        Stripe.setPublishableKey(this.publishableKey);
    }

    setPublishableKey(key: string): void {
        this.publishableKey = key; 
        if (this.available) 
            Stripe.setPublishableKey(this.publishableKey);
    }

    validateCardNumber(cardNumber: string): boolean {
        if (!this.available) 
            return false;
        
        return Stripe.validateCardNumber(cardNumber);
    }

    validateExpiry(month: string, year: string): boolean {
        if (!this.available) 
            return false;

        return Stripe.validateExpiry(month, year);
    }

    async validateCVC(cardCVC: string): Promise<boolean> {
        await this.apiReady();
        return Stripe.validateCVC(cardCVC);
    }

    async cardType(cardNumber: string): Promise<StripeCardDataBrand> {
        await this.apiReady();
        return Stripe.cardType(cardNumber);
    }

    async getToken(token: string): Promise<GetStripeTokenResponse> {

        await this.apiReady();
        return new Promise<GetStripeTokenResponse>((resolve, reject) => {
            Stripe.getToken(token, (status, response) => {
                resolve(new GetStripeTokenResponse(status, response));
            });
        })
    }

    get card(): StripeCard {
        // TODO: how to handle this with asynchronous API load
        if (!this.available)
            return null;
        return Stripe.card;
    }

    async createToken(data: StripeCardTokenData): Promise<CreateStripeTokenResponse> {
        await this.apiReady();

        return new Promise<CreateStripeTokenResponse>((resolve, reject) => {
            Stripe.createToken(data, (status, response) => {
                resolve(new CreateStripeTokenResponse(status, response));
            });
        })
    }

    get bankAccount(): StripeBankAccount {
        // TODO: how to handle this with asynchronous API load

        if (!this.available)
            return null;
        
        return Stripe.bankAccount;
    }
}